import Icon from "@components/ui/icon";
import { usePopoverClose } from "@components/ui/popover";
import { cn } from "@lib/cn";
import { endVariableDrag, startVariableDrag } from "../../insert/variable-drag";
import { usePlanLock } from "../hooks/use-plan-lock";
import type { PickerVariable, PlanTier } from "../types";
import { VariableTypeIcon } from "./variable-type-icon";

interface Props {
  variable: PickerVariable;
  onInsert?: (variable: PickerVariable) => void;
  /** Renders the row in its highlighted state (e.g. inline typeahead keyboard nav). */
  highlighted?: boolean;
}

const PLAN_CHIP: Record<PlanTier, string> = {
  starter: "tw-border-starter-border tw-bg-starter-base",
  pro: "tw-border-pro-border tw-bg-pro-base",
  team: "tw-border-team-border tw-bg-team-base",
};

export const VariableRow = ({ variable, onInsert, highlighted }: Props) => {
  const { isPlanLocked, openUpgrade } = usePlanLock();
  const closePopover = usePopoverClose();
  const locked = isPlanLocked(variable.plan);

  const handleClick = () => {
    if (locked && variable.plan) {
      openUpgrade(variable.plan, "editor-variable-picker", variable.id);
      closePopover?.();
      return;
    }
    onInsert?.(variable);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (locked) {
      e.preventDefault();
      return;
    }
    startVariableDrag(e, variable);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={endVariableDrag}
      aria-selected={highlighted}
      className={cn(
        "tw-bg-transparent tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded tw-border-none tw-px-2 tw-text-left tw-transition-colors hover:tw-bg-blue-50",
        highlighted && "tw-bg-blue-50",
      )}
    >
      <VariableTypeIcon type={variable.type} color={variable.defaultColor} />
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-normal tw-text-blue">
        {variable.name}
      </span>
      {locked && variable.plan && (
        <span
          className={cn(
            "tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid",
            PLAN_CHIP[variable.plan],
          )}
        >
          <Icon
            icon="ri-vip-crown-fill"
            className="!tw-text-xs !tw-text-white"
          />
        </span>
      )}
    </button>
  );
};
